/**
 * BlockerStore — the blockers feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY blockers module that talks to the API, and
 * it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<Blocker>('blockers')` + `api.identityProvider`). It never
 * imports ApiClient or calls fetch. React hooks call this store; pages call the
 * hook.
 *
 * Persistence model: each blocker is one document with id `blk_<uuid>` in the
 * real 'blockers' generic-store collection (DDB pointer row + S3 body). Every
 * field is short, so every field is mirrored onto the DDB pointer row's index
 * attributes and {@link listSummaries} reconstructs full blockers directly from
 * the pointer rows WITHOUT hydrating S3 bodies — which also lets the
 * {@link ./blockerImpact} selectors run over the summaries.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type { Blocker, BlockerDraft, BlockerSummary } from './types';
import {
  blockerDocId,
  isBlockerScope,
  isBlockerStatus,
  normalizeImpactDays,
} from './blockerModel';

export const BLOCKERS_COLLECTION = 'blockers';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  summary: 'summary',
  blockedStoryId: 'blockedStoryId',
  blockedWorkstreamId: 'blockedWorkstreamId',
  blockedEngineer: 'blockedEngineer',
  dependencyOwner: 'dependencyOwner',
  internalExternal: 'internalExternal',
  blockingSince: 'blockingSince',
  expectedResolution: 'expectedResolution',
  escalationOwner: 'escalationOwner',
  relatedTicket: 'relatedTicket',
  affectedGoalId: 'affectedGoalId',
  forecastImpactDays: 'forecastImpactDays',
  status: 'status',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  lastUpdated: 'lastUpdated',
  deleted: 'deleted',
} as const;

export class BlockerStore {
  private readonly docs: DocumentStore<Blocker>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Blocker>(BLOCKERS_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(blocker: Blocker): StringMap {
    return {
      [ROW.summary]: blocker.summary ?? '',
      [ROW.blockedStoryId]: blocker.blockedStoryId ?? '',
      [ROW.blockedWorkstreamId]: blocker.blockedWorkstreamId ?? '',
      [ROW.blockedEngineer]: blocker.blockedEngineer ?? '',
      [ROW.dependencyOwner]: blocker.dependencyOwner ?? '',
      [ROW.internalExternal]: blocker.internalExternal,
      [ROW.blockingSince]: blocker.blockingSince ?? '',
      [ROW.expectedResolution]: blocker.expectedResolution ?? '',
      [ROW.escalationOwner]: blocker.escalationOwner ?? '',
      [ROW.relatedTicket]: blocker.relatedTicket ?? '',
      [ROW.affectedGoalId]: blocker.affectedGoalId ?? '',
      [ROW.forecastImpactDays]:
        blocker.forecastImpactDays === undefined
          ? ''
          : String(blocker.forecastImpactDays),
      [ROW.status]: blocker.status,
      [ROW.createdBy]: blocker.createdBy ?? '',
      [ROW.createdAt]: blocker.createdAt ?? '',
      [ROW.lastUpdated]: blocker.lastUpdated ?? '',
      [ROW.deleted]: blocker.deleted ? 'true' : 'false',
    };
  }

  /** Reconstructs a full blocker projection from a pointer row. */
  private toSummary(row: StringMap): BlockerSummary {
    const scope = row[ROW.internalExternal] ?? '';
    const status = row[ROW.status] ?? '';
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      summary: row[ROW.summary] ?? '',
      blockedStoryId: row[ROW.blockedStoryId] || undefined,
      blockedWorkstreamId: row[ROW.blockedWorkstreamId] || undefined,
      blockedEngineer: row[ROW.blockedEngineer] ?? '',
      dependencyOwner: row[ROW.dependencyOwner] ?? '',
      internalExternal: isBlockerScope(scope) ? scope : 'INTERNAL',
      blockingSince: row[ROW.blockingSince] ?? '',
      expectedResolution: row[ROW.expectedResolution] || undefined,
      escalationOwner: row[ROW.escalationOwner] || undefined,
      relatedTicket: row[ROW.relatedTicket] || undefined,
      affectedGoalId: row[ROW.affectedGoalId] || undefined,
      forecastImpactDays: normalizeImpactDays(row[ROW.forecastImpactDays]),
      status: isBlockerStatus(status) ? status : 'OPEN',
      createdBy: row[ROW.createdBy] ?? '',
      createdAt: row[ROW.createdAt] ?? '',
      lastUpdated: row[ROW.lastUpdated] ?? '',
    };
  }

  /**
   * Lists all non-deleted blocker summaries. Filters to this collection
   * defensively (the underlying list is not collection-scoped).
   */
  async listSummaries(): Promise<BlockerSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === BLOCKERS_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single blocker document by id (hydrates the S3 body). */
  async get(id: string): Promise<Blocker | undefined> {
    return this.docs.get(id);
  }

  /**
   * Creates a new blocker. Seeds id, createdBy (from identity), createdAt and
   * lastUpdated, and returns the saved document.
   */
  async create(draft: BlockerDraft): Promise<Blocker> {
    const identity = await this.api.identityProvider.getIdentity();
    const blocker = this.fromDraft(blockerDocId(), draft, identity.alias, undefined);
    await this.docs.save(blocker.id, blocker, this.indexAttributes(blocker));
    return blocker;
  }

  /**
   * Applies a draft's fields to an existing blocker in place (same id/createdBy/
   * createdAt), refreshes lastUpdated, and re-mirrors index attributes.
   */
  async applyDraft(blocker: Blocker, draft: BlockerDraft): Promise<Blocker> {
    const updated = this.fromDraft(
      blocker.id,
      draft,
      blocker.createdBy,
      blocker.createdAt,
    );
    return this.update(updated);
  }

  /** Persists a full blocker document in place (refreshes lastUpdated). */
  async update(blocker: Blocker): Promise<Blocker> {
    const updated: Blocker = {
      ...blocker,
      lastUpdated: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /** Marks a blocker resolved (or reopens it). No-op-safe. */
  async setStatus(id: string, status: Blocker['status']): Promise<Blocker | undefined> {
    const existing = await this.docs.get(id);
    if (!existing) return undefined;
    if (existing.status === status) return existing;
    return this.update({ ...existing, status });
  }

  /**
   * Soft-deletes a blocker: fetches it, flags it deleted, and updates both the
   * S3 body and the pointer row so queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Blocker = { ...existing, deleted: true };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }

  /**
   * Builds a full Blocker from a draft, seeding id/createdBy and preserving
   * createdAt on edit (new entries stamp "now"). Empty optional text fields
   * collapse to undefined.
   */
  private fromDraft(
    id: string,
    draft: BlockerDraft,
    createdBy: string,
    existingCreatedAt: string | undefined,
  ): Blocker {
    const now = new Date().toISOString();
    const clean = (s?: string) => {
      const t = (s ?? '').trim();
      return t ? t : undefined;
    };
    return {
      id,
      summary: draft.summary.trim(),
      blockedStoryId: draft.blockedStoryId || undefined,
      blockedWorkstreamId: draft.blockedWorkstreamId || undefined,
      blockedEngineer: draft.blockedEngineer.trim(),
      dependencyOwner: draft.dependencyOwner.trim(),
      internalExternal: isBlockerScope(draft.internalExternal)
        ? draft.internalExternal
        : 'INTERNAL',
      blockingSince: draft.blockingSince.trim(),
      expectedResolution: clean(draft.expectedResolution),
      escalationOwner: clean(draft.escalationOwner),
      relatedTicket: clean(draft.relatedTicket),
      affectedGoalId: draft.affectedGoalId || undefined,
      forecastImpactDays: normalizeImpactDays(draft.forecastImpactDays),
      status: isBlockerStatus(draft.status) ? draft.status : 'OPEN',
      createdBy,
      createdAt: existingCreatedAt ?? now,
      lastUpdated: now,
      deleted: false,
    };
  }
}
